<template>
  <div>
    <h3>Gravitational Force</h3>
    <p>
      In 1665, Sir Issac Newton made a conclusion that any two bodies in this universe attract each other by virtue
      of their masses and this force of attraction is called gravitational force. Although this force is very weak
      for elementary particles, it is very important in the study of large objects. It helps us to study planetary orbits, motion of
      planets and their moons.
      It is the gravitational force due to which moons revolve around their planets and earth attracts every object towards its center.
      In 1687, Newton put forth a law called Newton’s Law of Gravitation.
    </p>
    <h3>Newton's Law of Gravitation</h3>
    <p>
      According to Newton's law of gravitation, every particle in the universe attracts other particle with a force
      directly proportional to the product of the masses between the two particles and inversely
      proportional to the square of distance between them.
    </p>
    <p>
      Thus, if m<sub>1</sub> and m<sub>2</sub> are the masses of the two bodies, and r is the distance between them,
      magnitude of the gravitational force \(F\) is given as,
      $$F \propto \frac{m_1m_2}{r^2} $$
      or
      $$ F= G\frac{m_1m_2}{r^2}$$
      where \(G\) is the proportionality constant and is known as gravitational constant. It is a universal constant
      and its value is equal to 6.673 x 10 <sup>-11</sup> N m<sup>2</sup>/kg<sup>2</sup>.
    </p>
    <p>
      Gravitational force is conservative in nature and is independent of the medium between the two particles. This
      law is only valid for point masses. However, for external points, even large spherical bodies act as
      particles in terms of the gravitational forces between them and their masses can be assumed to be
      concentrated at their centers.
    </p>
    <h3>Vector Form</h3>
    <p>
      Direction of force between the two objects is directed along the line joining them. Newton’s law
      of gravitation can also be written in vector form which will tell us about the direction of the two
      forces.
    </p>
    <v-img src="/assets\Gravitaion_image_1.jpg" width="400px" />

    <p>
      Consider the figure shown above. For the two masses m<sub>1</sub> and m<sub>2</sub>, whose position vectors
      are \(\mathbf{r_1}\) and \(\mathbf{r_2}\)  respectively, force in vector form is given as,
      $$\mathbf {F_{21}}=-G\frac{m_1m_2}{|\mathbf {r_{21}}|^2} \mathbf{\hat{r}_{21}}$$
      where,\(F_{21}\) is the force applied on object 2 by object 1, \(G\) is the gravitational
      constant and \(\hat{r}_{21}\) is the unit vector from object 1 to object 2.
      The negative sign indicates that the force is acting in a direction opposite to the unit vector.
    </p>
    <br>
    <p>
      $$\mathbf{\hat{r}_{21}}=\frac{\mathbf{r_2}-\mathbf{r_1}}{|\mathbf{r_2}-\mathbf{r_1}|}$$
      Therefore,
      $$\mathbf F_{21}=-G\frac{m_1m_2}{{|\mathbf{r_2}-\mathbf{r_1}|}^3} \mathbf{r_2}-\mathbf{r_1}$$
      Force on object 1 due to object 2 i.e, \(\mathbf F_{12}\) is given as,
      $$\mathbf F_{12}= G\frac{m_1m_2}{{|\mathbf{r_2}-\mathbf{r_1}|}^3} \mathbf{r_2}-\mathbf{r_1}$$
      It can also be seen that \(\mathbf F_{12} =-\mathbf F_{21}\).
    </p>
    <br><br>
    <p>
      In a system of n particles, the interaction force between any two particles remains unchanged and the net gravitational force on any particle can be given as the vector sum of all individual gravitational forces acting on the body i.e, if \(F\) is the net force on a body, it is given as,
      $$ \mathbf{F} = \mathbf{F_{12}}+\mathbf{F_{13}} +...+ \mathbf{F_{1n}}  $$
      This is called the <b>principle of superposition</b>.
    </p>
    <h3 ref="playgraph">
      MagicGraph | Law of Gravitation
    </h3>
    <v-responsive>
      <v-layout align-center justify-center>
        <div id="jxgbox1" class="edliy-box-about" />
      </v-layout>
    </v-responsive>
  </div>
</template>
<script>
import Boxes from './Boxes.js'
export default {
  name: 'FBD',
  created: function () {
    this.$store.commit('navigation/resetState');
    let title = 'Law of Gravitation';
    this.$store.commit('navigation/changeTitle', title);
    this.$store.commit('navigation/changeMenu', title);
    let newTopics = [
      {title: 'What is a Free Body Diagram?', img:'/assets/number-1.svg', action: () => this.goto('ia')},
      {title: 'Purpose of FBD', img:'/assets/number-2.svg', action: () => this.goto('ja')},
      {title: 'Example of FBD', img:'/assets/number-3.svg', action: () => this.goto('ka')},
      {title: 'MagicGraph' ,img:'/assets/touch.svg', action: () => this.goto('graphical')},
    ];
    this.$store.commit('navigation/replaceTopics', newTopics);
    let newshowhome = false;
    this.$store.commit('navigation/toggleshowhome', newshowhome);
    let newPhys =true;
    this.$store.commit('navigation/replacePhys', newPhys);
    let newLeftArrow =true;
    this.$store.commit('navigation/replaceLeftArrow', newLeftArrow);
    let newModule =true;
    this.$store.commit('navigation/replaceModule', newModule);
  },
  mounted () {
    MathJax.Hub.Queue(["Typeset", MathJax.Hub]);
    Boxes.box1();
  },
  metaInfo() {
  return{ title: 'Free Body Diagram',
          titleTemplate: '%s - Learn interactively',
          meta: [ {name: 'viewport', content: 'width=device-width, initial-scale=1'},
                  {name: 'description', content: 'Learn interactively how to draw a free body diagram'}
                ]
        }
   },
}
</script>
<style lang="scss">
@import 'src/styles/edliy-box.scss';
@import 'src/styles/subtopic-menu.scss';
@import 'src/styles/edliy-box-about.scss';
@import 'src/styles/screen-sizes.scss';
</style>
